import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { PaymentSettingType } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import {
  Deduction,
  GetDeductionListParams,
} from '@nxsys/store/slices/paymentSetting/types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreateDeductionModal } from '../create';
import { ViewDeductionModal } from '../detail';
import { getColumns } from './columns';
import { debounce } from 'lodash';

const DeductionListing = () => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const dispatch = useDispatch<AppDispatch>();

  const { deductionList } = useSelector(
    (state: AppState) => state.paymentSettingStore,
  );
  const { loading, list, pagination, queryParams } = deductionList;

  const [searchValue, setSearchValue] = useState('');

  const [openViewDeduction, setOpenViewDeduction] = useState<{
    open: boolean;
    data: Deduction | null;
  }>({
    open: false,
    data: null,
  });

  const [openCreateDeduction, setOpenCreateDeduction] = useState<{
    open: boolean;
  }>({
    open: false,
  });

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetDeductionListParams) => {
    dispatch(paymentSettingActions.getDeductionList(params));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const refetchData = useCallback(
    () => handleFetchData({ ...queryParams }),
    [queryParams],
  );

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['paymentSetting.deduction.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          onClick={toggleCreateDeduction}
          leftIcon={<PlusOutlined />}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const toggleViewPayment = () =>
    setOpenViewDeduction((prev) => ({
      ...prev,
      open: !prev.open,
    }));
  const handleViewDeduction = (data: Deduction) =>
    setOpenViewDeduction({
      open: true,
      data,
    });

  const toggleCreateDeduction = () =>
    setOpenCreateDeduction((prev) => ({
      ...prev,
      open: !prev.open,
    }));

  const isFiltering = !!queryParams?.search;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: isIreland ? 1800 : 1200, y: 700 }}
            titleEmpty={messages['paymentSetting.deduction.empty']}
            columns={getColumns({
              isBureau,
              isIreland,
              messages,
              paymentSettingType: PaymentSettingType.DEDUCTION,
              handleViewDeduction,
            })}
            onChangePagination={onChangePagination}
            noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
            addNewBtnOnClick={toggleCreateDeduction}
            dataTypeName={messages['common.deduction']}
          />
        </AppsContent>
      </AppCard>
      <ViewDeductionModal
        isOpen={openViewDeduction.open}
        toggle={toggleViewPayment}
        deductionId={openViewDeduction.data?.id}
        refetchData={refetchData}
      />
      {openCreateDeduction.open && (
        <CreateDeductionModal
          isOpen={openCreateDeduction.open}
          toggle={toggleCreateDeduction}
          refetchData={refetchData}
        />
      )}
    </AppsContainer>
  );
};

export default DeductionListing;
